import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  LayoutDashboard,
  Scissors,
  Image,
  Star,
  CalendarDays,
  MessageSquare,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  Loader2,
  Bell,
  CheckCircle,
  Sparkles,
  Megaphone,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Send,
  Phone,
  Users,
  Mail,
  CalendarPlus,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { API, SITE_URL, authHeaders, resolveUrl, ImageUploadField } from "./shared";

const EmailListTab = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [newSub, setNewSub] = useState({ name: "", email: "" });
  const [bulkText, setBulkText] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [broadcast, setBroadcast] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const subRes = await axios.get(`${API}/admin/subscribers`, authHeaders());
      setSubscribers(subRes.data);
    } catch (e) { console.error("Failed to load subscribers:", e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    if (!newSub.email) { toast.error("Email required"); return; }
    try {
      await axios.post(`${API}/admin/subscribers`, newSub, authHeaders());
      toast.success("Subscriber added!");
      setNewSub({ name: "", email: "" });
      fetchData();
    } catch { toast.error("Failed to add"); }
  };

  const handleBulkImport = async () => {
    if (!bulkText.trim()) { toast.error("Paste emails to import"); return; }
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/admin/subscribers/bulk`, { entries: bulkText }, authHeaders());
      toast.success(`${res.data.added} subscribers imported!`);
      setBulkText("");
      setShowBulk(false);
      fetchData();
    } catch { toast.error("Import failed"); }
    finally { setBulkLoading(false); }
  };

  const handleBroadcast = async () => {
    if (!broadcast.subject || !broadcast.message) {
      toast.error("Subject and message are required");
      return;
    }
    if (!window.confirm(`Send this email to ${subscribers.length} subscribers?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/broadcast`, broadcast, authHeaders());
      toast.success(`Email sent to ${res.data.sent} subscribers!`);
      setBroadcast({ subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const filtered = searchTerm
    ? subscribers.filter(s => s.name?.toLowerCase().includes(searchTerm.toLowerCase()) || s.email?.toLowerCase().includes(searchTerm.toLowerCase()))
    : subscribers;

  return (
    <div data-testid="admin-email-list-tab" className="space-y-6">
      <h2 className="font-serif text-2xl text-brand-text">Email List ({subscribers.length} subscribers)</h2>

      {/* Broadcast Email */}
      <div className="border border-brand-border bg-brand-surface p-5 space-y-4">
        <p className="font-sans text-xs text-brand-gold font-semibold uppercase tracking-wider">Send Email to All Clients</p>
        <p className="font-sans text-xs text-brand-text-muted">This email goes to all {subscribers.length} subscribers. Use {"{name}"} to personalize.</p>
        <Input
          value={broadcast.subject}
          onChange={(e) => setBroadcast({ ...broadcast, subject: e.target.value })}
          placeholder="Email subject (e.g. New Styles Available!)"
          data-testid="broadcast-subject"
          className="bg-brand-bg border-brand-border text-brand-text rounded-none"
        />
        <Textarea
          value={broadcast.message}
          onChange={(e) => setBroadcast({ ...broadcast, message: e.target.value })}
          placeholder="Your message... Use {name} to personalize."
          rows={6}
          data-testid="broadcast-message"
          className="bg-brand-bg border-brand-border text-brand-text rounded-none"
        />
        <button
          onClick={handleBroadcast}
          disabled={sending}
          data-testid="broadcast-send-btn"
          className="flex items-center gap-2 px-8 py-3 bg-brand-gold text-black font-sans text-sm font-semibold hover:bg-brand-gold-light transition-colors disabled:opacity-50"
        >
          <Mail size={16} /> {sending ? `Sending to ${subscribers.length}...` : `Send to All ${subscribers.length} Subscribers`}
        </button>
      </div>

      {/* Subscriber List */}
      <div className="border border-brand-border bg-brand-surface p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="font-sans text-xs text-brand-gold font-semibold uppercase tracking-wider">All Subscribers ({subscribers.length})</p>
          <div className="flex gap-2">
            <button
              onClick={() => { setShowBulk(!showBulk); setShowAdd(false); }}
              data-testid="bulk-import-btn"
              className="flex items-center gap-1 px-3 py-1.5 border border-brand-border text-brand-text-muted font-sans text-xs hover:border-brand-gold hover:text-brand-gold transition-colors"
            >
              <Users size={12} /> Bulk Import
            </button>
            <button
              onClick={() => { setShowAdd(!showAdd); setShowBulk(false); }}
              data-testid="add-subscriber-btn"
              className="flex items-center gap-1 px-3 py-1.5 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors"
            >
              <Plus size={12} /> Add Email
            </button>
          </div>
        </div>

        {/* Add Single */}
        {showAdd && (
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <Input value={newSub.name} onChange={(e) => setNewSub({ ...newSub, name: e.target.value })} placeholder="Client name" className="bg-brand-bg border-brand-border text-brand-text rounded-none flex-1" />
            <Input value={newSub.email} onChange={(e) => setNewSub({ ...newSub, email: e.target.value })} placeholder="Email address" className="bg-brand-bg border-brand-border text-brand-text rounded-none flex-1" data-testid="new-sub-email" />
            <button onClick={handleAdd} className="px-4 py-2 bg-brand-gold text-black font-sans text-xs font-semibold">Add</button>
          </div>
        )}

        {/* Bulk Import */}
        {showBulk && (
          <div className="mb-4 space-y-3 border border-brand-border p-4">
            <p className="font-sans text-xs text-brand-text-muted">Paste up to 500 emails. Use one of these formats:</p>
            <p className="font-sans text-[10px] text-brand-gold">Name | email@example.com (one per line) OR just email@example.com</p>
            <Textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={"Sarah Johnson | sarah@email.com\nAisha Williams | aisha@gmail.com\njane@email.com\nmary@email.com"}
              rows={8}
              data-testid="bulk-import-text"
              className="bg-brand-bg border-brand-border text-brand-text rounded-none font-mono text-xs"
            />
            <div className="flex items-center justify-between">
              <p className="font-sans text-[10px] text-brand-text-muted">{bulkText.split("\n").filter(l => l.trim() && l.includes("@")).length} emails detected</p>
              <button onClick={handleBulkImport} disabled={bulkLoading} className="px-6 py-2 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light disabled:opacity-50" data-testid="bulk-import-submit">
                {bulkLoading ? "Importing..." : "Import All"}
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name or email..."
          data-testid="subscriber-search"
          className="bg-brand-bg border-brand-border text-brand-text rounded-none mb-3"
        />

        <p className="font-sans text-[10px] text-brand-text-muted mb-2">Auto-collected from bookings, walk-ins, waitlist. New gallery posts are automatically emailed to all.</p>

        {/* List */}
        <div className="max-h-[500px] overflow-y-auto space-y-0">
          <div className="flex items-center gap-4 py-2 border-b border-brand-gold/30 sticky top-0 bg-brand-surface">
            <span className="font-sans text-[10px] text-brand-gold font-semibold flex-1">NAME</span>
            <span className="font-sans text-[10px] text-brand-gold font-semibold flex-1">EMAIL</span>
            <span className="font-sans text-[10px] text-brand-gold font-semibold w-16">SOURCE</span>
          </div>
          {filtered.map((sub, i) => (
            <div key={sub.email || i} className="flex items-center gap-4 py-1.5 border-b border-brand-border/30">
              <span className="font-sans text-xs text-brand-text flex-1 truncate">{sub.name || "—"}</span>
              <span className="font-sans text-xs text-brand-text-muted flex-1 truncate">{sub.email}</span>
              <span className="font-sans text-[10px] text-brand-gold capitalize w-16">{sub.source}</span>
            </div>
          ))}
          {filtered.length === 0 && <p className="font-sans text-xs text-brand-text-muted text-center py-4">No subscribers found.</p>}
        </div>
        {searchTerm && <p className="font-sans text-[10px] text-brand-text-muted mt-2">Showing {filtered.length} of {subscribers.length}</p>}
      </div>
    </div>
  );
};

export default EmailListTab;
